import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Building2, LogIn, PlusCircle } from 'lucide-react';
import { adminApi } from '../../lib/api-client';
import { useAuth } from '../../lib/auth-context';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { LoadingState, ErrorState, EmptyState, friendlyErrorMessage } from '../../components/ui/states';
import { useToast } from '../../components/ui/toast';
import type { WorkspaceSummary } from '../../lib/types';

/** No shared "relative date" helper exists elsewhere in this app yet — kept local since this is
 * currently the only call site; promote to `lib/` if a second one shows up. */
function formatRelativeDate(iso: string | null): string {
  if (!iso) return 'Never';
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString();
}

function openFindingsBadge(count: number) {
  if (count === 0) return <Badge variant="neutral">0</Badge>;
  return <Badge variant={count >= 5 ? 'danger' : 'warning'}>{count}</Badge>;
}

export function AdminConsole() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { impersonate } = useAuth();
  const [switchingInto, setSwitchingInto] = useState<string | null>(null);
  const query = useQuery({ queryKey: ['admin', 'workspaces'], queryFn: () => adminApi.listWorkspaces() });

  const impersonateMutation = useMutation({
    mutationFn: (id: string) => adminApi.impersonate(id),
    onSuccess: (result) => {
      impersonate({
        accessToken: result.accessToken,
        tenantId: result.tenantId,
        userId: '', // the impersonation token's own subject — the UI never needs this id directly
        role: result.role,
        workspaceName: result.tenantName,
      });
      navigate('/', { replace: true });
    },
    onError: (err) => {
      toast({ title: 'Could not switch into that workspace', description: friendlyErrorMessage(err), variant: 'error' });
      setSwitchingInto(null);
    },
  });

  const handleSwitchIn = (workspace: WorkspaceSummary) => {
    setSwitchingInto(workspace.id);
    impersonateMutation.mutate(workspace.id);
  };

  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const workspaces = query.data?.workspaces ?? [];

  return (
    <div>
      <PageHeader
        title="Admin Console"
        description="Every DefendIQ customer workspace, in one place."
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild>
              <Link to="/admin/platform-keys">Platform API keys</Link>
            </Button>
            <Button asChild>
              <Link to="/admin/workspaces/new">
                <PlusCircle size={16} /> New Workspace
              </Link>
            </Button>
          </div>
        }
      />

      {workspaces.length === 0 ? (
        <EmptyState
          icon={<Building2 size={32} />}
          title="No workspaces yet"
          description="Create the first customer workspace to get started."
          action={
            <Button asChild>
              <Link to="/admin/workspaces/new">
                <PlusCircle size={16} /> New Workspace
              </Link>
            </Button>
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border">
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-ink-faint">
                <th className="p-3 font-medium">Workspace</th>
                <th className="p-3 font-medium">Users</th>
                <th className="p-3 font-medium">Targets</th>
                <th className="p-3 font-medium">Open Findings</th>
                <th className="p-3 font-medium">Last Scan</th>
                <th className="p-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {workspaces.map((workspace) => (
                <tr key={workspace.id} className="border-b border-border last:border-0 hover:bg-surface">
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-ink">{workspace.name}</span>
                      <Badge variant="neutral" className="font-mono">
                        {workspace.slug}
                      </Badge>
                    </div>
                  </td>
                  <td className="p-3 text-ink-muted">{workspace.userCount}</td>
                  <td className="p-3 text-ink-muted">{workspace.targetCount}</td>
                  <td className="p-3">{openFindingsBadge(workspace.openFindingsCount)}</td>
                  <td className="p-3 text-ink-muted">{formatRelativeDate(workspace.lastScanAt)}</td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-2">
                      <Button asChild variant="outline" size="sm">
                        <Link to={`/admin/workspaces/${workspace.id}`}>Open</Link>
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleSwitchIn(workspace)}
                        disabled={impersonateMutation.isPending && switchingInto === workspace.id}
                      >
                        <LogIn size={14} /> Switch In
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
